import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AoeTemplate, ClientMessage } from '@roll1/shared';
import { getAffectedTokens } from '@roll1/shared/aoe-geometry';
import { cellCenter, fitSpellAoe, spellAoeAimMode, type SpellAoeFit } from '@roll1/shared/spell-aoe';
import { cancelSpellAoeFit, registerFitCanvas, tokenOnAnyCanvas, useSpellAoeFitRequest } from '../lib/spell-aoe-fit-store';

/** Fill of an area that may be kept / one that may not (beyond the spell range). */
export const FIT_COLOR_OK = 'rgba(255, 140, 40, 0.3)';
export const FIT_COLOR_NO = 'rgba(150, 150, 150, 0.3)';

/** The `template:place` message for an ordinary shared template (what the AoE tool sends, too). */
export function templatePlaceMessage(tpl: Omit<AoeTemplate, 'id' | 'placedBy'>, pageId?: string): Extract<ClientMessage, { type: 'template:place' }> {
  return {
    id: crypto.randomUUID(),
    type: 'template:place',
    timestamp: new Date().toISOString(),
    templateId: crypto.randomUUID(),
    shape: tpl.shape,
    originX: tpl.originX,
    originY: tpl.originY,
    sizeFt: tpl.sizeFt,
    rotation: tpl.rotation,
    color: tpl.color,
    label: tpl.label,
    ...(pageId ? { pageId } : {}),
  };
}

interface FitToken { id: string; x: number; y: number; size: number; visible?: boolean }

interface Options {
  /** Tokens of the map on screen. */
  tokens: readonly FitToken[];
  gridSize: number;
  gridSizeFt: number;
  /** Puts an ordinary shared template on the map (the `template:place` command). */
  place: (template: Omit<AoeTemplate, 'id' | 'placedBy'>) => void;
  /** Redraw the UI and token layers of the canvas. */
  redraw: () => void;
}

export interface UseSpellAoeFitReturn {
  active: boolean;
  spellName: string;
  rangeFt: number | null;
  aimed: boolean;
  fit: SpellAoeFit | null;
  /** The area to draw (null until the first aim). */
  preview: Omit<AoeTemplate, 'id' | 'placedBy'> | null;
  /** Tokens inside the preview, mapped to its color (the glow of the token layer). */
  affected: Map<string, string>;
  canKeep: boolean;
  /** How the area is aimed (see spellAoeAimMode); 'point-turn' needs a press, then a drag. */
  mode: ReturnType<typeof spellAoeAimMode> | null;
  /** phase: 'down' = a press / tap, 'move' = the pointer moved (hover, or a drag after a press). */
  aim: (worldX: number, worldY: number, snap: boolean, phase?: 'down' | 'move') => void;
  keep: () => void;
  cancel: () => void;
}

/**
 * The fit mode: the area of a spell from the caster token, aimed by the cursor / the finger. Nothing is sent until
 * «Оставить на карте» (then the ordinary `template:place`).
 */
export function useSpellAoeFit({ tokens, gridSize, gridSizeFt, place, redraw }: Options): UseSpellAoeFitReturn {
  const request = useSpellAoeFitRequest();
  const [aimPt, setAimPt] = useState<{ x: number; y: number } | null>(null);
  const [turnPt, setTurnPt] = useState<{ x: number; y: number } | null>(null);
  /** point-turn: the origin is fixed by a press; moves after it turn the area. */
  const lockedRef = useRef(false);
  const redrawRef = useRef(redraw);
  redrawRef.current = redraw;

  // A new request starts from no aim.
  useEffect(() => { setAimPt(null); setTurnPt(null); lockedRef.current = false; }, [request?.nonce]);

  // This canvas's tokens, for the other canvases to ask.
  const idsRef = useRef<ReadonlySet<string>>(new Set());
  idsRef.current = new Set(tokens.map(t => t.id));
  useEffect(() => registerFitCanvas(() => idsRef.current), []);

  const token = request ? tokens.find(t => t.id === request.tokenId) ?? null : null;
  // The caster is gone from the map (deleted, other page): the fit ends.
  // (Only when no canvas has it: with two canvases on screen the other one may be showing the caster.)
  useEffect(() => { if (request && !token && !tokenOnAnyCanvas(request.tokenId)) cancelSpellAoeFit(); }, [request, token]);

  // A token's x/y is its centre (the canvas draws it centred there).
  const tokenX = token ? token.x : 0;
  const tokenY = token ? token.y : 0;

  const fit = useMemo<SpellAoeFit | null>(() => {
    if (!request || !token) return null;
    const self = request.rangeFt === 0 && (request.aoe.shape === 'sphere' || request.aoe.shape === 'cylinder');
    if (!aimPt && !self) return null;
    return fitSpellAoe({
      aoe: request.aoe, rangeFt: request.rangeFt, tokenX, tokenY,
      aimX: aimPt?.x ?? tokenX, aimY: aimPt?.y ?? tokenY, turnX: turnPt?.x, turnY: turnPt?.y, gridSize, gridSizeFt,
    });
  }, [request, token, aimPt, turnPt, tokenX, tokenY, gridSize, gridSizeFt]);

  const color = fit?.outOfRange ? FIT_COLOR_NO : FIT_COLOR_OK;
  const preview = useMemo<Omit<AoeTemplate, 'id' | 'placedBy'> | null>(() => {
    if (!fit || !request) return null;
    return { shape: fit.shape, originX: fit.originX, originY: fit.originY, sizeFt: fit.sizeFt, rotation: fit.rotation, color, label: request.spellName };
  }, [fit, request, color]);

  const affected = useMemo(() => {
    const out = new Map<string, string>();
    if (!preview || fit?.outOfRange) return out;
    const ids = getAffectedTokens(
      { id: '__fit__', placedBy: '', ...preview },
      tokens.filter(t => t.visible !== false) as never,
      gridSize, gridSizeFt,
    );
    for (const id of ids) out.set(id, color);
    return out;
  }, [preview, fit, tokens, gridSize, gridSizeFt, color]);

  useEffect(() => { redrawRef.current(); }, [preview, affected, request]);

  // Esc cancels (not while typing).
  useEffect(() => {
    if (!request) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      cancelSpellAoeFit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request]);

  const mode = request ? spellAoeAimMode(request.aoe.shape, request.rangeFt) : null;

  const aim = useCallback((worldX: number, worldY: number, snap: boolean, phase: 'down' | 'move' = 'move') => {
    if (!request) return;
    const m = spellAoeAimMode(request.aoe.shape, request.rangeFt);
    // The centre / origin of a point area snaps to a cell centre (the cell the pointer is IN: floor, not round).
    const cell = (v: number): number => cellCenter(v, gridSize);
    if (m === 'point-turn') {
      if (phase === 'down') {
        lockedRef.current = true;
        setAimPt(snap ? { x: cell(worldX), y: cell(worldY) } : { x: worldX, y: worldY });
        setTurnPt(null);
      } else if (lockedRef.current) {
        setTurnPt({ x: worldX, y: worldY });
      } else {
        setAimPt(snap ? { x: cell(worldX), y: cell(worldY) } : { x: worldX, y: worldY });
      }
      return;
    }
    if (m === 'point' && snap) setAimPt({ x: cell(worldX), y: cell(worldY) });
    else setAimPt({ x: worldX, y: worldY });
  }, [request, gridSize]);

  const canKeep = !!fit && !fit.outOfRange;

  const keep = useCallback(() => {
    if (!fit || fit.outOfRange || !request) return;
    place({
      shape: fit.shape, originX: fit.originX, originY: fit.originY, sizeFt: fit.sizeFt,
      rotation: fit.rotation, color: FIT_COLOR_OK, label: request.spellName,
    });
    cancelSpellAoeFit();
  }, [fit, request, place]);

  return {
    active: !!request && !!token,
    spellName: request?.spellName ?? '',
    rangeFt: request?.rangeFt ?? null,
    aimed: !!aimPt,
    mode,
    fit, preview, affected, canKeep, aim, keep,
    cancel: cancelSpellAoeFit,
  };
}
